import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import { useAuthStore } from "../stores/authStore";
import type { Profile } from "../types/profile";
import { logger } from "../lib/logger";

const PROFILE_CACHE_KEY = "profile-cache";
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

interface CachedProfile {
  data: Profile;
  timestamp: number;
}

// Load profile from localStorage (only if it matches current user)
function loadProfileFromCache(userId?: string): Profile | null {
  try {
    const cached = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedProfile = JSON.parse(cached);
    const now = Date.now();

    // Check if cache belongs to current user
    if (userId && parsed.data.id !== userId) {
      // Different user - clear cache
      localStorage.removeItem(PROFILE_CACHE_KEY);
      return null;
    }

    // Check if cache is still valid
    if (now - parsed.timestamp < CACHE_DURATION) {
      return parsed.data;
    }

    // Cache expired, remove it
    localStorage.removeItem(PROFILE_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

// Save profile to localStorage
function saveProfileToCache(profile: Profile): void {
  try {
    const cache: CachedProfile = {
      data: profile,
      timestamp: Date.now()
    };
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    logger.error("Error saving profile to cache", error);
  }
}

// Fetch profile from server (creates if doesn't exist)
async function fetchProfile(): Promise<Profile> {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error("Not authenticated");
  }

  // Try to fetch existing profile
  const { data: existingProfile, error: fetchError } = await supabase
    .from("profiles")
    .select("id, email, full_name, avatar_url, username, has_completed_onboarding")
    .eq("id", user.id)
    .single();

  // If profile exists, return it
  if (existingProfile && !fetchError) {
    return existingProfile as Profile;
  }

  // Profile doesn't exist - create it
  if (fetchError && fetchError.code === "PGRST116") {
    logger.log("Profile not found, creating new profile for user:", user.id);
    
    const { data: newProfile, error: createError } = await supabase
      .from("profiles")
      .insert({
        id: user.id,
        email: user.email || "",
        full_name: null,
        avatar_url: null,
        username: null,
        has_completed_onboarding: false,
      })
      .select("id, email, full_name, avatar_url, username, has_completed_onboarding")
      .single();

    if (createError) {
      logger.error("Error creating profile", createError);
      throw createError;
  }

    return newProfile as Profile;
  }

  // Some other error occurred
  logger.error("Error loading profile", fetchError);
  throw fetchError;
}

// Hook to get profile
export function useProfile() {
  const { status, user } = useAuthStore();
  const isAuthenticated = status === "authenticated" && !!user;
  const userId = user?.id;

  return useQuery({
    queryKey: ["profile", userId], // Include userId in key to prevent cross-user caching
    queryFn: async () => {
      // Always fetch fresh for onboarding reliability
      const profile = await fetchProfile();
      saveProfileToCache(profile);
      return profile;
    },
    enabled: isAuthenticated, // Only fetch when authenticated
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter for reliability)
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch on window focus for freshness
    initialData: () => {
      return userId ? loadProfileFromCache(userId) ?? undefined : undefined;
    }
  });
}

// Hook to mark onboarding complete
export function useMarkOnboardingComplete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Not authenticated");
      }

      const { error } = await supabase
        .from("profiles")
        .update({ has_completed_onboarding: true })
        .eq("id", user.id);

      if (error) {
        logger.error("Error updating onboarding", error);
        throw error;
      }

      return true;
    },
    onSuccess: () => {
      // Invalidate and refetch profile
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    }
  });
}
